import React from "react";
import { User, Baby, BrainCircuit, ArrowRight } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const services = [{
  title: "Adult Psychiatry",
  description: "Comprehensive psychiatric evaluations and personalized medication management for adults. We specialize in treating anxiety, depression, ADHD, and mood disorders using a data-driven, empathetic approach that respects your unique life journey.",
  icon: <User className="h-8 w-8 text-primary" />,
  link: "/ContactPage"
}, {
  title: "Pediatric Psychiatry",
  description: "Compassionate, evidence-based care for children and adolescents. Our team addresses developmental challenges, behavioral issues, and emotional well-being, working closely with families to build a supportive foundation for growth.",
  icon: <Baby className="h-8 w-8 text-primary" />,
  link: "/ContactPage"
}, {
  title: "Geriatric & Complex Care",
  description: "Specialized treatment for complex cases, including treatment-resistant conditions and geriatric mental health. We utilize cutting-edge genetic evaluation to tailor solutions for neurocognitive disorders and intricate medication needs.",
  icon: <BrainCircuit className="h-8 w-8 text-primary" />,
  link: "/ContactPage"
}];
export default function CareForEveryStageOfLife() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll();
  return <section id="care-for-every-stage-of-life" className="relative w-full py-24 overflow-hidden bg-background">
      {}
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none">
        <div className="absolute top-[-10%] left-[-10%] w-[50%] h-[50%] rounded-full bg-primary/5 blur-3xl" />
        <div className="absolute bottom-[-10%] right-[-10%] w-[50%] h-[50%] rounded-full bg-accent/10 blur-3xl" />
        <div className="absolute top-[40%] left-[60%] w-[30%] h-[30%] rounded-full bg-secondary/20 blur-3xl" />
      </div>

      <div className="container relative mx-auto px-4 md:px-6">
        <div className="max-w-3xl mx-auto text-center mb-16">
          <h2 className="text-3xl md:text-4xl font-serif font-medium text-foreground mb-4">
            Care for Every Stage of Life
          </h2>
          <p className="text-lg text-muted-foreground">
            From early childhood development to complex adult and geriatric
            cases, we provide precision empathy at every step.
          </p>
        </div>

        <div ref={ref} className={`flex flex-col gap-6 transition-all duration-1000 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`}>
          {services.map((service, i) => <div key={i} data-index={i}>
              <Card className="group overflow-hidden border border-white/20 bg-white/60 dark:bg-black/40 backdrop-blur-md shadow-sm hover:shadow-md transition-all duration-300">
                <CardContent className="p-0">
                  <div className="flex flex-col md:flex-row items-center md:items-stretch">
                    {}
                    <div className="flex-shrink-0 p-8 flex items-center justify-center bg-primary/5 md:w-32 md:border-r border-border/50">
                      <div className="p-3 rounded-full bg-background shadow-sm ring-1 ring-border/50 group-hover:scale-110 transition-transform duration-300">
                        {service.icon}
                      </div>
                    </div>

                    {}
                    <div className="flex-grow p-6 md:p-8 flex flex-col justify-center text-center md:text-left">
                      <h3 className="text-xl font-semibold text-foreground mb-3">
                        {service.title}
                      </h3>
                      <p className="text-muted-foreground leading-relaxed">
                        {service.description}
                      </p>
                    </div>

                    {}
                    <div className="flex-shrink-0 p-6 md:p-8 flex items-center justify-center md:border-l border-border/50 w-full md:w-auto bg-transparent">
                      <Button asChild className="w-full md:w-auto">
                        <Link to={service.link}>
                          Book Evaluation
                          <ArrowRight className="ml-2 h-4 w-4" />
                        </Link>
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>)}
        </div>
      </div>
    </section>;
}